import React, { createRef, useCallback, useEffect, useState } from "react";
import { useHistory, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import classNames from "classnames";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import InfluencerIcon from "src/images/social/universal-user.svg";
import ReachIcon from "src/images/clients-full-grey.svg";
import LazyLoad from "react-lazy-load";
import { showToast } from "src/app/methods/showToast";
import Flag from "src/app/components/Flag/Flag";
import { SocialProvider, convertTikTokToWorkspaceVersion } from "src/types";
import InfiniteScroll from "react-infinite-scroller";
import { cdtScrollRef } from "src/app/pages/CreatorDiscoveryTool/CreatorDiscoveryTool";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";
import { ReactComponent as SearchIcon } from "src/images/magnifier-white.svg";
import NoResultsScreen from "src/app/components/NoResultsScreen/NoResultsScreen";
import { Button } from "src/app/components/Button/Button";
import EmptyResultIcon from "src/images/empty-audience-result.png";
import personImage from "src/images/person.svg";
import errorImg from "src/images/something_went_wrong.svg";
import { setCreatorSearchLockedModalVisibility } from "src/redux";
import ImagesChecker from "src/app/components/ImagesChecker/ImagesChecker";
import { Img } from "src/app/components/Img/Img";
import { gallerySocialProviders } from "src/app/components/Gallery/components/GalleryFiltersDropdown/utils";
import {
  CDTable,
  CDTableBody,
  CDTableColumn,
  CDTableHead,
  CDTableRow,
} from "../../components/CDTable/CDTable";
import {
  clearSelectedUsers,
  getVelocitySummaryData,
  searchAudience,
  selectInfluencer,
  selectMultipleInfluencer,
} from "../audiencesManagerActions";
import METHODS from "../../helpers/methods";
import UserInterestList from "../UserInterestList/UserInterestList";
import VelocityValue from "../VelocityValue/VelocityValue";
import InfluencerInfo from "../../components/InfluencerInfo/InfluencerInfo";
import Loader from "../../components/Loader/Loader";
import { generateRelevanceTooltip } from "../../helpers/velocityIndexTooltip";
import AudienceResultLoading from "./AudienceResultLoading/AudienceResultLoading";
import BrandSafetyItem from "../BrandSafety/BrandSafetyItem";
import PendingUsersHandler from "./PendingUsersHandler/PendingUsersHandler";
import Consts from "../../helpers/consts";
import AudiencesResultsCheckboxWrapper from "./components/AudiencesResultsCheckboxWrapper";
import AudiencesResultsCheckbox from "./components/AudiencesResultsCheckbox";
import CDVelocitySummaryModal from "../VelocitySummaryModal/containers/CDVelocitySummaryModal";
import { GoToSocial } from "../components/GoToSocial";
import { usePageTitle } from "src/app/title-management/usePageTitle";

import "./AudiencesResults.scss";

function AudiencesResults(props) {
  const {
    sortBy,
    searchData,
    getListData,
    activeTab,
    userList,
    infoData,
    similarProfiles,
    selectedInterest,
    searchAudienceFilters,
    selectedInfluencersOnResults,
    selectedInfluencersOnList,
    pendingUsers,
    influencersInListInfo,
    hasAccessToInfluencerInsightsToolVelocityIndex,
    audienceLoading,
    awaitingUsers,
    listSocialProvider,
    listId,
    brandSafetySettings,
    getMoreResults,
    listLoading,
    isAudienceDataError,
    toggleFilters,
    hasAccessToFullExports,
    lastFetchWasEmpty,
    isResultsLimitReached,
    pageName,
  } = props;

  usePageTitle(pageName);

  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedAllSimilar, setSelectedAllSimilar] = useState(false);
  const [showVelocitySummary, setShowVelocitySummary] = useState(false);
  const [selectedInfluencers, setSelectedInfluencers] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  const handleRangeSelect = (start, end, list, value) => {
    const minIndex = Math.min(start, end);
    const maxIndex = Math.max(start, end);

    const selectionIndexes = [];
    for (let i = minIndex; i <= maxIndex; i++) {
      selectionIndexes.push(i);
    }

    const userIds = [];
    const profileUrls = [];
    selectionIndexes.forEach((index) => {
      const influencer = list[index];
      if (influencer) {
        userIds.push(influencer.userId);
        profileUrls.push(influencer.profileUrl);
      }
    });

    props.selectMultipleInfluencer(userIds, profileUrls, activeTab, value);
  };

  const history = useHistory();

  const totalCount = infoData?.totalResults || 0;

  let endOfList;
  const minTotalCount = Math.min(totalCount, Consts.audienceSearchResultLimit);

  if (isAudienceDataError) {
    endOfList = true;
  } else if (lastFetchWasEmpty) {
    // Backend returned empty array - no more results available
    endOfList = true;
  } else if (activeTab === "results" && !hasAccessToFullExports) {
    endOfList = userList.length >= minTotalCount;
  } else {
    endOfList = userList.length >= totalCount;
  }

  useEffect(() => {
    setSelectedInfluencers(
      activeTab === "results"
        ? selectedInfluencersOnResults
        : selectedInfluencersOnList,
    );
  }, [activeTab, selectedInfluencersOnResults, selectedInfluencersOnList]);

  const checkIfPreselected = useCallback(
    (userId) => {
      return influencersInListInfo?.userIds?.indexOf(userId) > -1;
    },
    [influencersInListInfo],
  );

  const preselectedUsersCount = useCallback(() => {
    return userList.filter((user) => {
      return checkIfPreselected(user.userId);
    }).length;
  }, [userList, checkIfPreselected]);

  const selectedUsersOnPageCount = useCallback(() => {
    return userList.filter((user) => {
      return selectedInfluencers?.indexOf(user.userId) > -1;
    }).length;
  }, [userList, selectedInfluencers]);

  const selectedSimilarProfilesOnPageCount = useCallback(() => {
    return similarProfiles?.filter((user) => {
      return selectedInfluencers?.indexOf(user.userId) > -1;
    }).length;
  }, [similarProfiles, selectedInfluencers]);

  useEffect(() => {
    setSelectedAll(false);
    setSelectedAllSimilar(false);

    if (activeTab === "selected") {
      const isAnyNotMatchItem = userList.some(
        (item) => !item.filterMatchSummary?.doesResultMatchAllFilters,
      );

      if (isAnyNotMatchItem) {
        showToast(
          "warning",
          <IDHFormattedMessage id="ws_warning" defaultMessage="Warning" />,
          <IDHFormattedMessage
            id="front_some_of_the_creators_dont_match"
            defaultMessage="Some of the Creators don’t match one or more of the specified search criteria."
          />,
        );
      }
    }
  }, [userList, activeTab]);

  useEffect(() => {
    if (activeTab === "results") {
      if (
        selectedUsersOnPageCount() + preselectedUsersCount() ===
        userList.length
      ) {
        setSelectedAll(true);
      }
      if (
        similarProfiles.length &&
        selectedSimilarProfilesOnPageCount() === similarProfiles.length
      ) {
        setSelectedAllSimilar(true);
      }
    } else {
      if (selectedUsersOnPageCount() === userList.length) {
        setSelectedAll(true);
      }
    }

    if (selectedInfluencers.length === 0) {
      setSelectedAllSimilar(false);
    }

    if (!selectedUsersOnPageCount()) {
      setSelectedAll(false);
    }
  }, [
    selectedInfluencers,
    userList,
    similarProfiles,
    activeTab,
    preselectedUsersCount,
    selectedSimilarProfilesOnPageCount,
    selectedUsersOnPageCount,
  ]);

  const sortCompareFunction = (a, b, sortByValue) => {
    if (!sortByValue) {
      return 0;
    }

    const compareValues = (valA, valB) => {
      if (valA > valB) {
        return -1;
      }
      if (valA < valB) {
        return 1;
      }
      return 0;
    };

    switch (sortByValue) {
      case "credibility":
        return compareValues(a.credibility, b.credibility);
      case "velocityIndex":
        return compareValues(a.velocityIndex, b.velocityIndex);
      case "followers":
        return compareValues(
          parseFloat(a.followersCount),
          parseFloat(b.followersCount),
        );
      case "avgEr":
        return compareValues(
          parseFloat(a.avgErPercent),
          parseFloat(b.avgErPercent),
        );
      case "recentlyAdded":
        return compareValues(
          new Date(a.createdAt).getTime(),
          new Date(b.createdAt).getTime(),
        );
      default:
        return 0;
    }
  };

  const onSelectAllClick = async (list, value, setValue) => {
    const userIds = [];
    const profileUrls = [];

    if (activeTab === "results") {
      list.forEach((influencer) => {
        if (!checkIfPreselected(influencer.userId)) {
          userIds.push(influencer.userId);
          profileUrls.push(influencer.profileUrl);
        }
      });
    } else {
      list.forEach((influencer) => {
        userIds.push(influencer.userId);
        profileUrls.push(influencer.profileUrl);
      });
    }

    props.selectMultipleInfluencer(userIds, profileUrls, activeTab, !value);
    setValue(!value);
  };

  const selectUserClick = (userId, profileUrl) => {
    if (activeTab === "results") {
      if (
        selectedUsersOnPageCount() + preselectedUsersCount() ===
        userList.length
      ) {
        setSelectedAll(false);
      }
    } else {
      if (selectedUsersOnPageCount() === userList.length) {
        setSelectedAll(false);
      }
    }

    props.selectInfluencer(userId, profileUrl, activeTab);
  };

  const openVelocitySummary = (userId) => {
    props.getVelocitySummaryData(userId, searchData);
    setShowVelocitySummary(true);
  };

  const closeVelocitySummary = () => {
    setShowVelocitySummary(false);
  };

  const getColSpanNumber = () => {
    if (
      searchData?.influencerSocialProvider.value === "instagram" &&
      brandSafetySettings.keywords?.length > 0 &&
      activeTab === "selected"
    ) {
      return 6;
    }
    if (
      searchData?.influencerSocialProvider.value === "instagram" ||
      (searchData?.influencerSocialProvider.value === "tik_tok" &&
        brandSafetySettings.keywords?.length > 0 &&
        activeTab === "selected")
    ) {
      return 5;
    }
    return 4;
  };

  const getColumns = (list, checked, setChecked) => [
    list.length > 0
      ? {
          id: "front_select_page",
          defaultMessage: "Select page",
          empty: true,
          selectAll: {
            checked,
            onChange: () => onSelectAllClick(list, checked, setChecked),
            disabled:
              activeTab === "results" &&
              !list.filter((user) => {
                return !checkIfPreselected(user.userId);
              }).length,
          },
        }
      : { id: "front_select_page", empty: true },
    {
      id: "preview",
      empty: true,
      className: "cdt-table__image cdt-table__image--small",
    },
    {
      id: "front_username",
      defaultMessage: "Username",
      toLeft: true,
    },
    searchData?.influencerSocialProvider.value === "instagram"
      ? {
          id: "front_interests",
          defaultMessage: "Interests",
        }
      : null,
    {
      id: "front_recent_posts",
      defaultMessage: "Recent posts",
    },
    brandSafetySettings.keywords?.length > 0 &&
    influencersInListInfo?.totalResults <= Consts.audienceListPageSize &&
    activeTab === "selected"
      ? {
          id: "front_competitors_post_check",
          className: "competitors-post-check-column",
          defaultMessage: "Competitors Post Check",
        }
      : null,
    !Consts.socialProvidersWithDisabledAvgER.includes(
      searchData?.influencerSocialProvider?.value,
    )
      ? {
          id: "front_avg_er",
          defaultMessage: "avg. ER",
          tooltip: {
            id: "front_avg_er_tooltip",
            defaultMessage: "avg. ER tooltip",
          },
        }
      : null,
    !Consts.socialProvidersWithDisabledRelevance.includes(
      searchData?.influencerSocialProvider?.value,
    )
      ? {
          id: "front_relevance",
          defaultMessage: "relevance",
          tooltip: generateRelevanceTooltip(
            searchData?.influencerSocialProvider.value,
          ),
        }
      : null,
  ];

  const openProfile = (socialProvider, username) => {
    const pathNameWithProfile = `${window.location.pathname}/profile/${socialProvider}/${username}`;
    const searchParams = new URLSearchParams(window.location.search);
    const shouldShowMoreByDefault = gallerySocialProviders.find(
      (provider) => provider.value === socialProvider.replace("_", ""),
    );
    if (shouldShowMoreByDefault) {
      searchParams.set("galleryShowMoreByDefault", true);
    }
    const search = searchParams.toString();

    history.push({
      pathname: pathNameWithProfile,
      search,
    });
  };

  const renderList = (list, isSimilarProfiles = false) => {
    return list
      .sort((a, b) =>
        sortCompareFunction(a, b, activeTab === "selected" ? sortBy : null),
      )
      .map((influencer, index) => {
        const headerRef = createRef(null);
        const isPreselected =
          activeTab === "results" && checkIfPreselected(influencer.userId);
        const isSelected = selectedInfluencers.includes(influencer.userId);
        return (
          <CDTableRow key={influencer.userId} highlighted={isSelected}>
            <CDTableColumn className="cdt-table__checkbox">
              <AudiencesResultsCheckboxWrapper isPreselected={isPreselected}>
                <AudiencesResultsCheckbox
                  influencer={influencer}
                  selectedInfluencers={selectedInfluencers}
                  selectUserClick={selectUserClick}
                  handleRangeSelect={(start, end, value) => {
                    const list = isSimilarProfiles ? similarProfiles : userList;
                    handleRangeSelect(start, end, list, value);
                  }}
                  lastSelectedIndex={lastSelectedIndex}
                  setLastSelectedIndex={setLastSelectedIndex}
                  isPreselected={isPreselected}
                  index={index}
                />
              </AudiencesResultsCheckboxWrapper>
            </CDTableColumn>
            <CDTableColumn>
              <LazyLoad offset={300}>
                <div className="influencer-photo">
                  <Img
                    className={classNames(
                      "influencer-photo__img",
                      "cdt-table__image-content",
                      "cdt-table__image",
                    )}
                    src={influencer.profilePictureUrl}
                    onClick={() => headerRef.current.click()}
                    fallbackImageSrc={personImage}
                  />
                  {influencer.country && (
                    <Flag countryCode={influencer.country} />
                  )}
                </div>
              </LazyLoad>
            </CDTableColumn>
            <CDTableColumn>
              <InfluencerInfo
                socialProvider={influencer.socialProvider}
                socialProviders={influencer.socialProviders}
                username={influencer.socialUsername}
                headerRef={headerRef}
                wsGlobalTaskUuid={influencer?.wsGlobalTaskUuid}
                influencerName={influencer?.name}
              />
            </CDTableColumn>
            {searchData?.influencerSocialProvider.value === "instagram" && (
              <CDTableColumn>
                <UserInterestList
                  interestList={influencer.audienceInterests}
                  selectedInterest={selectedInterest}
                  visibleLimit={3}
                />
              </CDTableColumn>
            )}
            <CDTableColumn>
              <LazyLoad>
                <div
                  className="audience-results__recent-posts"
                  onClick={() =>
                    openProfile(
                      influencer.socialProvider,
                      influencer.socialUsername,
                    )
                  }
                >
                  <ImagesChecker
                    imageClassName="cdt-table__image-content post-preview"
                    data={influencer.lastPosts || []}
                    accessorKey="thumbnailSrc"
                    // TikTok-only HEIC recovery, see task:
                    // https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
                    allowHeicRecovery={
                      convertTikTokToWorkspaceVersion(
                        influencer.socialProvider,
                      ) === SocialProvider.TikTok
                    }
                    // Rendered at 80px (.cdt-table__image-content in AudiencesResults.scss).
                    heicRecoveryMaxDimension={80}
                    noImagesFallback={
                      <GoToSocial
                        socialProfileUrl={influencer.profileUrl}
                        socialProvider={influencer.socialProvider}
                      />
                    }
                  />
                </div>
              </LazyLoad>
            </CDTableColumn>
            {brandSafetySettings?.keywords?.length > 0 &&
            activeTab === "selected" ? (
              <CDTableColumn className="cdt-table__brand-safety">
                <BrandSafetyItem
                  status={influencer.competitorsCheckData.brandSafetyStatus}
                  competitorsPostDetected={
                    influencer.competitorsCheckData.brandSafetyStatus !==
                      "Pending" &&
                    Object.keys(influencer.competitorsCheckData.keywords || {})
                      .length > 0
                  }
                  keywords={influencer.competitorsCheckData.keywords}
                  username={influencer.socialUsername}
                  count={influencer.competitorsCheckData.count}
                />
              </CDTableColumn>
            ) : null}
            {!Consts.socialProvidersWithDisabledAvgER.includes(
              searchData?.influencerSocialProvider?.value,
            ) && (
              <CDTableColumn className="cdt-table__number cdt-table__number--medium">
                {Number(influencer.avgErPercent)
                  ? `${influencer.avgErPercent}%`
                  : "-"}
              </CDTableColumn>
            )}

            <CDTableColumn className="cdt-table__number cdt-table__number--medium cdt-table__number--light">
              {!Consts.socialProvidersWithDisabledRelevance.includes(
                searchData?.influencerSocialProvider?.value,
              ) && (
                <VelocityValue
                  value={influencer.velocityIndex}
                  onClick={
                    hasAccessToInfluencerInsightsToolVelocityIndex
                      ? () =>
                          influencer.velocityIndex !== null
                            ? openVelocitySummary(influencer.userId)
                            : null
                      : null
                  }
                  filterMatchSummary={influencer.filterMatchSummary}
                  isVelocitySummaryModalOpen={showVelocitySummary}
                />
              )}
            </CDTableColumn>
          </CDTableRow>
        );
      });
  };

  const renderSimilarProfiles = () => {
    if (similarProfiles.length && activeTab === "results") {
      return (
        <>
          <div className="audience-results-page__similar-profile-tabs">
            <div className="audience-results-page__tabs">
              <Tabs>
                <Tab
                  tabText={
                    <IDHFormattedMessage
                      id="front_similar_profiles"
                      defaultMessage="Similar profiles"
                    />
                  }
                  count={similarProfiles.length}
                  active
                  big
                  noClickable
                />
              </Tabs>
            </div>
          </div>

          <CDTableHead
            columns={getColumns(
              similarProfiles,
              selectedAllSimilar,
              setSelectedAllSimilar,
            )}
          />
          <CDTableBody>{renderList(similarProfiles, true)}</CDTableBody>
        </>
      );
    }

    return null;
  };

  const renderTable = () => {
    return (
      <CDTable
        columns={getColumns(userList, selectedAll, setSelectedAll)}
        autoLayout
        showNoResults={
          activeTab === "selected"
            ? userList.length + awaitingUsers.length === 0
            : userList.length === 0
        }
        customNoResults={
          activeTab === "selected" ? (
            <div className="empty-influencer-list">
              <div className="empty-influencer-list__left-side">
                <IDHFormattedMessage
                  id="front_creator_list_is_empty"
                  defaultMessage="Creator list is empty. <br>
                  Use Creator Search to find and add creators to the list."
                />

                <div>
                  <Button
                    variant="blue"
                    size="big"
                    textWithIcon
                    onClick={() => {
                      if (props.identity?.isDemoWorkspace) {
                        props.setCreatorSearchLockedModalVisibility(true);
                        return;
                      }
                      props.tabChange("results");
                    }}
                  >
                    <SearchIcon width={20} height={20} />
                    <IDHFormattedMessage
                      id="front_go_to_search_reasults"
                      defaultMessage="Go to search results"
                    />
                  </Button>
                </div>
              </div>
              <div className="empty-influencer-list__right-side">
                <img src={EmptyResultIcon} className="" alt="No results" />
              </div>
            </div>
          ) : isAudienceDataError ? (
            <div className="no-results-screen-wrapper">
              <NoResultsScreen
                title={
                  <IDHFormattedMessage
                    id="front_audience_results_data_error_title"
                    defaultMessage="Oops. Something went wrong..."
                  />
                }
                subtitle={
                  <IDHFormattedMessage
                    id="front_audience_results_data_error_description"
                    defaultMessage="An unexpected error occurred. Please try again."
                  />
                }
                bottomContent={
                  <Button size="large" variant="blue" onClick={toggleFilters}>
                    <IDHFormattedMessage
                      id="front_open_filters"
                      defaultMessage="Open filters"
                    />
                  </Button>
                }
                imgUrl={errorImg}
              />
            </div>
          ) : (
            <PendingUsersHandler
              influencerSocialUsername={
                searchAudienceFilters.influencerSocialUsername
              }
              getListData={getListData}
              pendingUsers={pendingUsers}
            />
          )
        }
      >
        <CDTableBody>
          {activeTab === "selected" &&
            awaitingUsers.map((user) => (
              <AudienceResultLoading
                key={user.socialUsername}
                username={user.socialUsername}
                showAsNonExistent={user.showAsNonExistent}
                status={user.status}
                id={user.id}
                listId={listId}
                listSocialProvider={listSocialProvider}
                competitorsCheckEnabled={
                  Object.keys(brandSafetySettings.keywords).length > 0 &&
                  activeTab === "selected"
                }
              />
            ))}
          {renderList(userList)}

          {userList.length > 0 && activeTab === "selected" && endOfList && (
            <CDTableRow>
              <CDTableColumn colSpan={getColSpanNumber()} className="to-left">
                <div className="audience-results__summary">
                  <div className="audience-results__summary-column">
                    <div>
                      {activeTab === "results" ? (
                        <IDHFormattedMessage
                          id="front_total_influencers_found"
                          defaultMessage="Total influencers found"
                        />
                      ) : (
                        <IDHFormattedMessage
                          id="front_total_influencers"
                          defaultMessage="Total influencers"
                        />
                      )}
                    </div>
                    <div>
                      <IDHFormattedMessage
                        id="front_total_potential_reach"
                        defaultMessage="Total potential reach"
                      />
                    </div>
                  </div>
                  <div className="audience-results__summary-column">
                    <div>
                      <img src={InfluencerIcon} alt="" />
                      {infoData?.totalResults >= 10000
                        ? "9999+"
                        : METHODS.splitNumbers(infoData?.totalResults)}
                    </div>
                    <div>
                      <img src={ReachIcon} alt="" />
                      {infoData?.totalPotentialReach > 0
                        ? METHODS.splitNumbers(infoData?.totalPotentialReach)
                        : "-"}
                    </div>
                  </div>
                </div>
              </CDTableColumn>
              {!Consts.socialProvidersWithDisabledAvgER.includes(
                searchData?.influencerSocialProvider?.value,
              ) && (
                <CDTableColumn className="cdt-table__number cdt-table__number--medium">
                  {Number(infoData?.totalAvgErPercent)
                    ? `${infoData?.totalAvgErPercent}%`
                    : "-"}
                </CDTableColumn>
              )}
              <CDTableColumn className="cdt-table__number cdt-table__number--medium cdt-table__number--light">
                {!Consts.socialProvidersWithDisabledRelevance.includes(
                  searchData?.influencerSocialProvider?.value,
                ) && <VelocityValue value={infoData?.totalAvgVelocityIndex} />}
              </CDTableColumn>
            </CDTableRow>
          )}
        </CDTableBody>

        {renderSimilarProfiles()}
      </CDTable>
    );
  };

  const renderResultsLimitInfo = () => {
    if (
      activeTab !== "results" ||
      !isResultsLimitReached ||
      userList.length === 0
    ) {
      return null;
    }

    return (
      <div className="audience-results__limit-info">
        <NoResultsScreen
          title={
            <IDHFormattedMessage
              id="front_creator_search_results_limit_title"
              defaultMessage="Looking for more creators?"
            />
          }
          subtitle={
            <IDHFormattedMessage
              id="front_creator_search_results_limit_subtitle"
              defaultMessage="Refine your filters to narrow your search and discover additional matches."
            />
          }
          bottomContent={
            <Button size="large" variant="blue" onClick={toggleFilters}>
              <IDHFormattedMessage
                id="front_refine_filters"
                defaultMessage="Refine filters"
              />
            </Button>
          }
          imgUrl={EmptyResultIcon}
        />
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === "results") {
      return (
        <>
          <InfiniteScroll
            pageStart={0}
            initialLoad={false}
            loadMore={() => getMoreResults(endOfList)}
            className="audience-results__scroller"
            hasMore={!endOfList}
            loader={<Loader />}
            useWindow={false}
            getScrollParent={() => cdtScrollRef.current}
          >
            {renderTable()}
          </InfiniteScroll>
          {renderResultsLimitInfo()}
        </>
      );
    }

    return renderTable();
  };

  if (audienceLoading && !listLoading) {
    return <Loader />;
  }
  return (
    <div className="audience-results">
      {renderContent()}
      {showVelocitySummary && (
        <CDVelocitySummaryModal onClose={closeVelocitySummary} />
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  activeWorkspaceUuid: state.mainReducer.activeWorkspaceUuid,
  searchAudienceFilters: state.audiencesManagerReducer.searchAudienceFilters,
  selectedInfluencersOnResults:
    state.audiencesManagerReducer.selectedInfluencersOnResults,
  selectedInfluencersOnList:
    state.audiencesManagerReducer.selectedInfluencersOnList,
  pendingUsers: state.audiencesManagerReducer.pendingUsers,
  influencersInListInfo: state.audiencesManagerReducer.influencersInListInfo,

  hasAccessToInfluencerInsightsToolVelocityIndex:
    state.audiencesManagerReducer
      .hasAccessToInfluencerInsightsToolVelocityIndex,
  audienceLoading: state.audiencesManagerReducer.audienceLoading,
  awaitingUsers: state.audiencesManagerReducer.awaitingUsers,
  listSocialProvider: state.audiencesManagerReducer.listSocialProvider,
  brandSafetySettings: state.audiencesManagerReducer.brandSafetySettings,
  isAudienceDataError: state.audiencesManagerReducer.isAudienceDataError,
  identity: state.mainReducer.identity,
  hasAccessToFullExports: state.audiencesManagerReducer.hasAccessToFullExports,
  lastFetchWasEmpty: state.audiencesManagerReducer.lastFetchWasEmpty,
  isResultsLimitReached: state.audiencesManagerReducer.isResultsLimitReached,
});

const mapDispatchToProps = (dispatch) => {
  return {
    searchAudience: (searchData, page, sortBy) =>
      dispatch(searchAudience(searchData, page, sortBy)),
    getVelocitySummaryData: (userId, searchData) =>
      dispatch(getVelocitySummaryData(userId, searchData)),
    selectInfluencer: (userId, profileUrl, type) =>
      dispatch(selectInfluencer(userId, profileUrl, type)),
    selectMultipleInfluencer: (userIds, profileUrls, type, value) =>
      dispatch(selectMultipleInfluencer(userIds, profileUrls, type, value)),
    clearSelectedUsers: (userIds, profileUrls, type) =>
      dispatch(clearSelectedUsers(userIds, profileUrls, type)),
    setCreatorSearchLockedModalVisibility: (creatorSearchLockedModalVisible) =>
      dispatch(
        setCreatorSearchLockedModalVisibility(creatorSearchLockedModalVisible),
      ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(AudiencesResults));
